import React from 'react';
import { format } from 'date-fns';
import { Eye } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Badge,
  Pagination,
} from '../ui';
import type { Ticket } from '../../types/ticket';
import type { PaginationInfo } from '../../types/api';

interface TicketListProps {
  tickets: Ticket[];
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onView: (ticket: Ticket) => void;
}

export const TicketList: React.FC<TicketListProps> = ({
  tickets,
  pagination,
  onPageChange,
  onView,
}) => {
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'open':
        return 'success';
      case 'pending':
        return 'warning';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open':
        return 'Aberto';
      case 'pending':
        return 'Pendente';
      case 'closed':
        return 'Fechado';
      default:
        return status;
    }
  };

  return (
    <div>
      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Total de registros: {pagination.total}
      </div>
      <TableContainer>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Título</TableHead>
              <TableHead>Contato</TableHead>
              <TableHead>Assistente</TableHead>
              <TableHead>Atendente</TableHead>
              <TableHead>Fila</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Última Mensagem</TableHead>
              <TableHead>Criado em</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tickets.map((ticket) => (
              <TableRow key={ticket.id}>
                <TableCell>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {ticket.id}
                  </span>
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {ticket.title}
                    </div>
                    {ticket.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {ticket.description}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-gray-700 dark:text-gray-300">
                    {ticket.contact_name || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-gray-700 dark:text-gray-300">
                    {ticket.assistant_name || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-gray-700 dark:text-gray-300">
                    {ticket.user_account_name || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-gray-700 dark:text-gray-300">
                    {ticket.queue_name || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusVariant(ticket.status)}>
                    {getStatusLabel(ticket.status)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {ticket.last_message_at ? (
                      <>
                        <div className="text-gray-900 dark:text-gray-100">
                          {format(new Date(ticket.last_message_at), 'dd/MM/yyyy')}
                        </div>
                        <div className="text-gray-500">
                          {format(new Date(ticket.last_message_at), 'HH:mm')}
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div className="text-gray-900 dark:text-gray-100">
                      {format(new Date(ticket.created_at), 'dd/MM/yyyy')}
                    </div>
                    <div className="text-gray-500">
                      {format(new Date(ticket.created_at), 'HH:mm')}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onView(ticket)}
                      title="Ver detalhes"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <div className="mt-4">
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.last_page}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};
